import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";

// Local imports
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseButtonSecondary from "../BaseButtonSecondary";
import BaseInputText from "../BaseInputText";
import BaseInputTextArea from "../BaseInputTextArea";
import BaseInputSelect from "../BaseInputSelect";
import BaseInputNumber from "../BaseInputNumber";
import StyledSwitchGroup from "../StyledSwitchGroup";
import StyledMessage from "../StyledMessage";
import LoyaltyProgramHelpDialog from "./LoyaltyProgramHelpDialog";
import {
  LoyaltyProgramTemplateSchema,
  LoyaltyProgramTemplateFormData,
  LoyaltyProgramType,
  TriggerEvent,
  RewardType,
  getReadableProgramType,
  getReadableTriggerEvent,
  getReadableRewardType,
  ExistingLoyaltyProgramTemplate,
} from "../../models/loyalty-program-template";
import { MoreRewardsConfig } from "../../database/more-rewards";
import { DbRead } from "../../database";
import { ExistingSiteKeyLocation } from "../../models/site-key-location";
import { logger } from "../../logging";

interface Props {
  // Form configuration
  mode: "create" | "edit";
  initialData?: ExistingLoyaltyProgramTemplate;

  // Form handlers
  onSubmit: (data: LoyaltyProgramTemplateFormData) => Promise<void>;
  onCancel: () => void;

  // Loading state
  isLoading?: boolean;

  // Validation errors
  submitError?: string | null;

  // MoreRewards integration
  moreRewardsConfig?: MoreRewardsConfig | null;
  siteKeyID: string;
  siteKeyLocations?: ExistingSiteKeyLocation[];
}

export default function LoyaltyProgramForm({
  mode,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  submitError,
  moreRewardsConfig,
  siteKeyID,
  siteKeyLocations = [],
}: Props) {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    reset,
  } = useForm<LoyaltyProgramTemplateFormData>({
    resolver: zodResolver(LoyaltyProgramTemplateSchema),
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      isActive: initialData?.isActive ?? true,
      programType: initialData?.programType || LoyaltyProgramType.POINTS_BASED,
      triggerEvent: initialData?.triggerEvent || TriggerEvent.INVOICE_PAID,
      rewardType: initialData?.rewardType || RewardType.POINTS,
      rewardValue: initialData?.rewardValue || 1,
      maxRewardPerJob: initialData?.maxRewardPerJob || undefined,
      maxRewardPerInvoice: initialData?.maxRewardPerInvoice || undefined,
      maxRewardPerCustomer: initialData?.maxRewardPerCustomer || undefined,
      validityPeriod: initialData?.validityPeriod || undefined,
      minimumSpend: initialData?.minimumSpend || undefined,
      integrationConfig: initialData?.integrationConfig || undefined,
    },
  });

  // Watch form values for dynamic behavior
  const watchedProgramType = watch("programType");
  const watchedRewardType = watch("rewardType");
  const watchedTriggerEvent = watch("triggerEvent");
  const watchedIntegrationConfig = watch("integrationConfig");

  // MoreRewards state
  const [availableOfferCodes, setAvailableOfferCodes] = useState<
    { offerCode: string; offerName?: string }[]
  >([]);
  const [isLoadingOffers, setIsLoadingOffers] = useState(false);
  const [offersError, setOffersError] = useState<string | null>(null);

  // Help dialog state
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);

  // Filter out non-recommended options
  const recommendedProgramTypes = Object.values(LoyaltyProgramType).filter(
    (type) => type !== LoyaltyProgramType.RECURRING_DISCOUNT,
  );

  const recommendedRewardTypes = Object.values(RewardType).filter(
    (type) => type !== RewardType.FREE_SERVICE,
  );

  // Fetch MoreRewards offer codes
  const fetchOfferCodes = async () => {
    if (!moreRewardsConfig) return;

    setIsLoadingOffers(true);
    setOffersError(null);
    try {
      // Collect offers from all locations
      const allOffers: { offerCode: string; offerName?: string }[] = [];

      for (const location of siteKeyLocations) {
        try {
          const response = await DbRead.moreRewards.getOffers(
            siteKeyID,
            location.id,
          );
          if (response.success && response.offers) {
            // Add offers from this location
            response.offers.forEach((offer) => {
              allOffers.push({
                offerCode: offer.offerCode,
                offerName: offer.offerName,
              });
            });
          }
        } catch (locationError) {
          logger.warn(
            `Failed to fetch offers for location ${location.id}:`,
            locationError,
          );
          // Continue with other locations
        }
      }

      // Extract unique offer codes
      const uniqueOffers = Array.from(
        new Map(allOffers.map((offer) => [offer.offerCode, offer])).values(),
      );

      setAvailableOfferCodes(uniqueOffers);
    } catch (error) {
      logger.error("Error fetching MoreRewards offer codes:", error);
      setOffersError("Failed to load offer codes");
    } finally {
      setIsLoadingOffers(false);
    }
  };

  // Fetch offer codes when MoreRewards is enabled
  useEffect(() => {
    const isMoreRewardsEnabled =
      watchedIntegrationConfig?.moreRewards?.enabled === true;

    if (
      isMoreRewardsEnabled &&
      moreRewardsConfig &&
      availableOfferCodes.length === 0
    ) {
      fetchOfferCodes();
    }
  }, [watchedIntegrationConfig?.moreRewards?.enabled, moreRewardsConfig]);

  // Reset form when initial data changes (for edit mode)
  useEffect(() => {
    if (initialData && mode === "edit") {
      reset({
        name: initialData.name,
        description: initialData.description,
        isActive: initialData.isActive,
        programType: initialData.programType,
        triggerEvent: initialData.triggerEvent,
        rewardType: initialData.rewardType,
        rewardValue: initialData.rewardValue,
        maxRewardPerJob: initialData.maxRewardPerJob,
        maxRewardPerInvoice: initialData.maxRewardPerInvoice,
        maxRewardPerCustomer: initialData.maxRewardPerCustomer,
        validityPeriod: initialData.validityPeriod,
        minimumSpend: initialData.minimumSpend,
        integrationConfig: initialData.integrationConfig,
      });
    }
  }, [initialData, mode, reset]);

  const handleFormSubmit = async (data: LoyaltyProgramTemplateFormData) => {
    try {
      await onSubmit(data);
    } catch (error) {
      logger.error("Error submitting loyalty program form:", error);
    }
  };

  // Get reward value label based on reward type
  const getRewardValueLabel = (rewardType: RewardType): string => {
    switch (rewardType) {
      case RewardType.POINTS:
        return "Points per Dollar";
      case RewardType.PERCENTAGE_DISCOUNT:
        return "Discount Percentage (%)";
      case RewardType.FIXED_DISCOUNT:
        return "Fixed Discount Amount ($)";
      case RewardType.COMMISSION:
        return "Commission Percentage (%)";
      case RewardType.FREE_SERVICE:
        return "Services Required for Free Service";
      default:
        return "Reward Value";
    }
  };

  // Get reward value help text
  const getRewardValueHelp = (rewardType: RewardType): string => {
    switch (rewardType) {
      case RewardType.POINTS:
        return "Number of points earned per dollar spent";
      case RewardType.PERCENTAGE_DISCOUNT:
        return "Percentage discount applied to the invoice";
      case RewardType.FIXED_DISCOUNT:
        return "Fixed dollar amount discounted from the invoice";
      case RewardType.COMMISSION:
        return "Percentage commission paid to referrer";
      case RewardType.FREE_SERVICE:
        return "Number of services required before earning a free service";
      default:
        return "The value of the reward";
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          {/* Header */}
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {mode === "create"
                ? "Create Loyalty Program"
                : "Edit Loyalty Program"}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {mode === "create"
                ? "Configure a new loyalty program to reward your customers."
                : "Update the loyalty program configuration."}
            </p>
          </div>

          {/* Submit Error */}
          {submitError && (
            <StyledMessage type="error">
              {{ message: submitError }}
            </StyledMessage>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <BaseInputText
                    id="program-name"
                    text="Program Name"
                    required
                    admin
                    placeholder="e.g., VIP Points Program"
                    {...field}
                  />
                )}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="sm:col-span-2">
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <BaseInputTextArea
                    text="Description"
                    required
                    admin
                    rows={3}
                    inputName="description"
                    placeholder="Describe how this loyalty program works..."
                    {...field}
                  />
                )}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.description.message}
                </p>
              )}
            </div>

            <div>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center">
                    <StyledSwitchGroup
                      id="is-active"
                      name="isActive"
                      readableName="Active Program"
                      checked={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      ref={field.ref}
                    />
                    <div className="ml-3">
                      <p className="text-sm text-gray-600">
                        Active programs will automatically process rewards
                      </p>
                    </div>
                  </div>
                )}
              />
            </div>

            <div>
              <Controller
                name="sendLoyaltyNotificationEmail"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center">
                    <StyledSwitchGroup
                      id="send-loyalty-notification-email"
                      name="sendLoyaltyNotificationEmail"
                      readableName="Send Notification Emails"
                      checked={field.value ?? false}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      ref={field.ref}
                    />
                    <div className="ml-3">
                      <p className="text-sm text-gray-600">
                        Automatically send email notifications to customers when
                        they earn rewards
                      </p>
                    </div>
                  </div>
                )}
              />
            </div>
          </div>

          {/* Program Configuration */}
          <div className="border-t border-gray-200 pt-6">
            <div className="mb-4 flex items-center justify-between">
              <h4 className="text-md font-medium text-gray-900">
                Program Configuration
              </h4>
              <button
                type="button"
                onClick={() => setIsHelpDialogOpen(true)}
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-500"
              >
                <QuestionMarkCircleIcon className="h-4 w-4" />
                Learn about loyalty programs
              </button>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <Controller
                  name="programType"
                  control={control}
                  render={({ field }) => (
                    <BaseInputSelect
                      id="program-type"
                      inputName="programType"
                      text="Program Type"
                      required
                      admin
                      {...field}
                    >
                      {recommendedProgramTypes.map((type) => (
                        <option key={type} value={type}>
                          {getReadableProgramType(type)}
                        </option>
                      ))}
                    </BaseInputSelect>
                  )}
                />
                {errors.programType && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.programType.message}
                  </p>
                )}
              </div>

              <div>
                <Controller
                  name="triggerEvent"
                  control={control}
                  render={({ field }) => (
                    <BaseInputSelect
                      id="trigger-event"
                      inputName="triggerEvent"
                      text="Trigger Event"
                      required
                      admin
                      {...field}
                    >
                      {Object.values(TriggerEvent).map((trigger) => (
                        <option key={trigger} value={trigger}>
                          {getReadableTriggerEvent(trigger)}
                        </option>
                      ))}
                    </BaseInputSelect>
                  )}
                />
                {errors.triggerEvent && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.triggerEvent.message}
                  </p>
                )}
              </div>

              <div>
                <Controller
                  name="rewardType"
                  control={control}
                  render={({ field }) => (
                    <BaseInputSelect
                      id="reward-type"
                      inputName="rewardType"
                      text="Reward Type"
                      required
                      admin
                      {...field}
                    >
                      {recommendedRewardTypes.map((type) => (
                        <option key={type} value={type}>
                          {getReadableRewardType(type)}
                        </option>
                      ))}
                    </BaseInputSelect>
                  )}
                />
                {errors.rewardType && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.rewardType.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Reward Configuration */}
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-md mb-4 font-medium text-gray-900">
              Reward Configuration
            </h4>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <Controller
                  name="rewardValue"
                  control={control}
                  render={({ field }) => (
                    <BaseInputNumber
                      id="reward-value"
                      text={getRewardValueLabel(watchedRewardType)}
                      required
                      admin
                      min={0}
                      step={watchedRewardType === RewardType.POINTS ? 1 : 0.01}
                      placeholder="Enter reward value"
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          field.onChange("");
                        } else {
                          const numValue = Number(value);
                          field.onChange(isNaN(numValue) ? value : numValue);
                        }
                      }}
                    />
                  )}
                />
                <p className="mt-1 text-sm text-gray-500">
                  {getRewardValueHelp(watchedRewardType)}
                </p>
                {errors.rewardValue && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.rewardValue.message}
                  </p>
                )}
              </div>

              <div>
                <Controller
                  name="minimumSpend"
                  control={control}
                  render={({ field }) => (
                    <BaseInputNumber
                      id="minimum-spend"
                      text="Minimum Spend ($)"
                      admin
                      min={0}
                      step={0.01}
                      placeholder="No minimum"
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(
                          value === "" ? undefined : Number(value),
                        );
                      }}
                    />
                  )}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Minimum invoice amount required to earn rewards
                </p>
                {errors.minimumSpend && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.minimumSpend.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Constraints */}
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-md mb-1 font-medium text-gray-900">
              Reward Limits
            </h4>
            <p className="mb-6 mt-1 text-sm text-gray-500">
              Set limits on the amount of rewards a customer can receive from
              this program. You can set multiple limits for different scenarios.
            </p>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <Controller
                  name="maxRewardPerJob"
                  control={control}
                  render={({ field }) => (
                    <BaseInputNumber
                      id="max-reward-per-job"
                      text="Max Reward Per Job"
                      admin
                      min={0}
                      step={watchedRewardType === RewardType.POINTS ? 1 : 0.01}
                      placeholder="No limit"
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(
                          value === "" ? undefined : Number(value),
                        );
                      }}
                    />
                  )}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Total amount of rewards a customer can receive on any single
                  job from this program
                </p>
                {errors.maxRewardPerJob && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.maxRewardPerJob.message}
                  </p>
                )}
              </div>

              <div>
                <Controller
                  name="maxRewardPerInvoice"
                  control={control}
                  render={({ field }) => (
                    <BaseInputNumber
                      id="max-reward-per-invoice"
                      text="Max Reward Per Invoice"
                      admin
                      min={0}
                      step={watchedRewardType === RewardType.POINTS ? 1 : 0.01}
                      placeholder="No limit"
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(
                          value === "" ? undefined : Number(value),
                        );
                      }}
                    />
                  )}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Total amount of rewards a customer can receive on any single
                  invoice from this program
                </p>
                {errors.maxRewardPerInvoice && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.maxRewardPerInvoice.message}
                  </p>
                )}
              </div>

              <div>
                <Controller
                  name="maxRewardPerCustomer"
                  control={control}
                  render={({ field }) => (
                    <BaseInputNumber
                      id="max-reward-per-customer"
                      text="Max Active Reward Per Customer"
                      admin
                      min={0}
                      step={watchedRewardType === RewardType.POINTS ? 1 : 0.01}
                      placeholder="No limit"
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(
                          value === "" ? undefined : Number(value),
                        );
                      }}
                    />
                  )}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Total amount of rewards a customer can have active at once
                  from this program
                </p>
                {errors.maxRewardPerCustomer && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.maxRewardPerCustomer.message}
                  </p>
                )}
              </div>

              <div>
                <Controller
                  name="validityPeriod"
                  control={control}
                  render={({ field }) => (
                    <BaseInputNumber
                      id="validity-period"
                      text="Validity Period (Days)"
                      admin
                      min={1}
                      step={1}
                      placeholder="No expiration"
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(
                          value === "" ? undefined : Number(value),
                        );
                      }}
                    />
                  )}
                />
                <p className="mt-1 text-sm text-gray-500">
                  How long rewards remain valid after earning
                </p>
                {errors.validityPeriod && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.validityPeriod.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* MoreRewards Integration */}
          {moreRewardsConfig && (
            <div className="border-t border-gray-200 pt-6">
              <div className="mb-4 flex items-center gap-3">
                <img
                  src="/moreRewards_logo.svg"
                  alt="MoreRewards"
                  className="h-8"
                />
                <h4 className="text-md font-medium text-gray-900">
                  Integration
                </h4>
              </div>

              <div className="space-y-4">
                <div>
                  <Controller
                    name="integrationConfig.moreRewards.enabled"
                    control={control}
                    render={({ field }) => (
                      <StyledSwitchGroup
                        id="more-rewards-enabled"
                        name="moreRewardsEnabled"
                        readableName="Enable MoreRewards Integration"
                        checked={field.value || false}
                        onChange={(checked) => {
                          field.onChange(checked);
                          if (checked) {
                            // Initialize the integration config when enabled
                            setValue("integrationConfig", {
                              moreRewards: {
                                enabled: true,
                                offerCode: "",
                              },
                            });
                          } else {
                            // Clear the integration config when disabled
                            setValue("integrationConfig", undefined);
                          }
                        }}
                        onBlur={field.onBlur}
                      />
                    )}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Connect this loyalty program to your MoreRewards account
                  </p>
                </div>

                {watchedIntegrationConfig?.moreRewards?.enabled && (
                  <div>
                    <Controller
                      name="integrationConfig.moreRewards.offerCode"
                      control={control}
                      render={({ field }) => (
                        <BaseInputSelect
                          id="more-rewards-offer-code"
                          inputName="offerCode"
                          text="MoreRewards Offer Code"
                          required
                          admin
                          {...field}
                        >
                          <option value="">Select an offer code</option>
                          {isLoadingOffers ? (
                            <option disabled>Loading offer codes...</option>
                          ) : (
                            availableOfferCodes.map((offer) => (
                              <option
                                key={offer.offerCode}
                                value={offer.offerCode}
                              >
                                {offer.offerCode}
                                {offer.offerName && ` - ${offer.offerName}`}
                              </option>
                            ))
                          )}
                        </BaseInputSelect>
                      )}
                    />
                    {offersError && (
                      <p className="mt-1 text-sm text-red-600">{offersError}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      Select the MoreRewards offer code to associate with this
                      loyalty program
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 border-t border-gray-200 pt-6">
            <BaseButtonSecondary
              type="button"
              onClick={onCancel}
              disabled={isLoading || isSubmitting}
            >
              Cancel
            </BaseButtonSecondary>
            <BaseButtonPrimary
              type="submit"
              disabled={isLoading || isSubmitting}
              isBusy={isLoading || isSubmitting}
              busyText={mode === "create" ? "Creating..." : "Saving..."}
            >
              {mode === "create" ? "Create Program" : "Save Changes"}
            </BaseButtonPrimary>
          </div>
        </form>
      </div>

      {/* Help Dialog */}
      <LoyaltyProgramHelpDialog
        isOpen={isHelpDialogOpen}
        onClose={() => setIsHelpDialogOpen(false)}
      />
    </div>
  );
}
